import React from 'react';
import { Field, useFormikContext } from 'formik';

import { AutoCompleteSelectUI, CheckboxUI, FormErrorUI, FormLabelUI, InputUI, SelectUI } from '../../Interface';
import { DocumentDirectionField } from '../../../config/DocumentDirection';
import { EmailReplyToLevelField } from '../../../config/email';
import { generateAutocompleteItems } from '../../../utils/helper';
import useConfig from '../../../context/ConfigContext';
import { ElectronicInvoiceMethod } from '../../../config/electronicInvoice';

const DocumentTypeFields = ({ viewField, selectedValues, autocompleteOptions }) => {
  const { values, validateField, setFieldValue, errors } = useFormikContext();
  const { config } = useConfig();
  const mobileTargetCustomStatusOptions = generateAutocompleteItems(
    (selectedValues?.customDocStatus ?? []).filter((status) => status.active),
    'name',
    'code'
  );
  const mobileTargetCustomStatusLabel =
    selectedValues?.customDocStatus?.find((status) => status.code === values.mobileTargetCustomStatus)?.name || '';
  return (
    <div className="grid grid-cols-4 gap-x-4 gap-y-4 mx-4 mb-5 items-center">
      <div className="order-[1] col-span-4 md:col-span-1">
        <Field
          type="text"
          name="documentTypeId"
          as={InputUI}
          disabled={true}
          fullWidth
          label={<FormLabelUI text="Obj. kód" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.documentTypeId} />}
          InputLabelProps={{ shrink: values.documentTypeId !== '' }}
        />
      </div>

      <div className="order-[2] col-span-4 md:col-span-3">
        <Field
          type="text"
          name="name"
          as={InputUI}
          disabled={true}
          fullWidth
          label={<FormLabelUI text="Bizonylattípus neve" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.name} />}
          InputLabelProps={{ shrink: values.name !== '' }}
        />
      </div>

      <div className="order-[3] col-span-4 md:col-span-1">
        <Field
          name="direction"
          component={SelectUI}
          fullWidth
          option={DocumentDirectionField}
          value={values.direction ?? ''}
          variant="standard"
          onChange={(e) => {
            const value = e.target.value;
            setFieldValue('direction', value ?? null).then(() => {
              validateField('direction');
            });
          }}
          label={<FormLabelUI text="Irány" />}
          helperText={<FormErrorUI message={errors.direction} />}
          disabled={true}
          InputLabelProps={{ shrink: values.direction !== '' }}
        />
      </div>
      <div className="order-[4] col-span-4 md:col-span-2">
        <Field
          type="autocomplete"
          name="defaultItemCode"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Alapértelmezett cikk" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.defaultItemCode} />}
          onChange={(e, newVal) => {
            setFieldValue('defaultItemCode', newVal?.value ?? null).then(() => {
              validateField('defaultItemCode');
            });
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.defaultItemCode !== '' }}
          selectedValue={values.defaultItemCode}
          dataset={autocompleteOptions?.items}
        />
      </div>
      <div className="order-[6] col-span-4 md:col-span-1">
        <Field
          type="autocomplete"
          name="defaultWarehouse"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Alapértelmezett raktár" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.defaultWarehouse} />}
          onChange={(e, newVal) => {
            setFieldValue('defaultWarehouse', newVal?.value ?? null).then(() => {
              validateField('defaultWarehouse');
            });
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.defaultWarehouse !== '' }}
          selectedValue={values.defaultWarehouse}
          dataset={autocompleteOptions?.warehouse}
        />
      </div>
      <div className="order-[14] col-span-4 md:col-span-1">
        <Field
          type="autocomplete"
          name="mobileTargetCustomStatus"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Mobil cél egyedi státusz" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.mobileTargetCustomStatus} />}
          onChange={(_e, newVal) => {
            setFieldValue('mobileTargetCustomStatus', newVal?.value ?? null).then(() => {
              validateField('mobileTargetCustomStatus');
            });
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.mobileTargetCustomStatus !== '' }}
          selectedValue={values.mobileTargetCustomStatus}
          selectedLabelValue={mobileTargetCustomStatusLabel}
          dataset={mobileTargetCustomStatusOptions}
        />
      </div>
      <div className="order-[21] col-span-4 md:col-span-1">
        <Field
          name="defaultReplyToLevel"
          component={SelectUI}
          fullWidth
          option={EmailReplyToLevelField}
          value={values.defaultReplyToLevel ?? ''}
          variant="standard"
          onChange={(e) => {
            const value = e.target.value;
            setFieldValue('defaultReplyToLevel', value ?? null).then(() => {
              validateField('defaultReplyToLevel');
            });
          }}
          label={<FormLabelUI text="Alapértelmezett válaszcím szintje" />}
          helperText={<FormErrorUI message={errors.defaultReplyToLevel} />}
          disabled={viewField}
          InputLabelProps={{ shrink: values.defaultReplyToLevel !== '' }}
        />
      </div>
      <div className="order-[22] col-span-4 md:col-span-1">
        <Field
          type="text"
          name="replyToAddress"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Válaszcím" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.replyToAddress} />}
          disabled={viewField}
          InputLabelProps={{ shrink: values.replyToAddress !== '' }}
        />
      </div>
      <div className="order-[23] col-span-4 md:col-span-2">
        <Field
          type="text"
          name="projectFolder"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Projektmappa neve" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.projectFolder} />}
          disabled={viewField}
          InputLabelProps={{ shrink: values.projectFolder !== '' }}
        />
      </div>
      <div className="order-[7] col-span-4 md:col-span-1">
        <Field
          name="inventoryAccounting"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Készletkönyvelés" />}
          disabled={true}
        />
      </div>
      <div className="order-[8] col-span-2 md:col-span-1">
        <Field
          name="inventoryMovement"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Készletmozgás" />}
          disabled={viewField}
        />
      </div>
      <div className="order-[16] col-span-2 md:col-span-1">
        <Field
          name="company"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Cég" />}
          disabled={viewField}
        />
      </div>
      <div className="order-[17] col-span-2 md:col-span-1">
        <Field
          name="project"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Projekt" />}
          disabled={viewField}
        />
      </div>
      <div className="order-[18] col-span-2 md:col-span-1">
        <Field
          name="cooperation"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Kooperáció" />}
          disabled={viewField}
        />
      </div>
      <div className="order-[19] col-span-2 md:col-span-1">
        <Field
          name="employee"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Dolgozó" />}
          disabled={viewField}
        />
      </div>
      <div className="order-[13] col-span-2 md:col-span-1">
        <Field
          name="active"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Aktív" />}
          disabled={viewField}
        />
      </div>
      <div className="order-[11] col-span-2 md:col-span-1">
        <Field
          name="enableSettlement"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Elszámolás" />}
          disabled={viewField}
        />
      </div>
      <div className="order-[11] col-span-2 md:col-span-1">
        <Field
          name="disableSettlementRecheck"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Kiegyenlítés újravizsgálásának tiltása" />}
          disabled={viewField}
        />
      </div>
      <div className="order-[12] col-span-2 md:col-span-1">
        <Field
          name="useBankAccount"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Bankszámla" />}
          disabled={viewField}
        />
      </div>
      <div className="order-[5] col-span-2 md:col-span-1">
        <Field
          name="editableItemName"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Cikkmegnevezés átírható" />}
          disabled={viewField}
        />
      </div>
      <div className="order-[9] col-span-2 md:col-span-1">
        <Field
          name="additionalCost"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Mellékköltség" />}
          disabled={viewField}
        />
      </div>
      <div className="order-[10] col-span-2 md:col-span-1">
        <Field
          name="invoice"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Számla" />}
          disabled={viewField}
        />
      </div>
      {config?.digitalSigningEnabled && config?.electronicInvoiceMethod === ElectronicInvoiceMethod.ESZIGNO && (
        <div className="order-[10] col-span-2 md:col-span-1">
          <Field
            name="electronicSignatureEnabled"
            type="checkbox"
            as={CheckboxUI}
            fullWidth
            label={<FormLabelUI text="E-aláírható bizonylattípus" />}
            disabled={viewField}
          />
        </div>
      )}
      <div className="order-[10] col-span-2 md:col-span-1">
        <Field
          name="invoiceConfirmation"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Számlaigazolás" />}
          disabled={viewField}
        />
      </div>
      <div className="order-[10] col-span-2 md:col-span-1">
        <Field
          name="manualDocNum"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Kézi bizonylatszám" />}
          disabled={viewField}
        />
      </div>
      <div className="order-[12] col-span-2 md:col-span-1">
        <Field
          name="useDocumentPriority"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Bizonylatprioritás használata" />}
          disabled={viewField}
        />
      </div>
      <div className="order-[10] col-span-2 md:col-span-1">
        <Field
          name="disableDatePrefill"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Dátumok előtöltésének tiltása" />}
          disabled={viewField}
        />
      </div>
      <div className="order-[10] col-span-2 md:col-span-1">
        <Field
          name="showInternalNote"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Belső megjegyzés megjelenítése" />}
          disabled={viewField}
        />
      </div>
      <div className="order-[15] col-span-4 border-t border-labelColor opacity-50" />
      <div className="order-[14] col-span-4 md:col-span-2">
        <Field
          name="copySourceAttachments"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Forrásbizonylat mellékleteinek átvétele" />}
          disabled={viewField}
        />
      </div>
      <div className="order-[20] col-span-4 border-t border-labelColor opacity-50" />
    </div>
  );
};

export default DocumentTypeFields;
